import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import styles from './ProductListPage.module.css';
import FallbackImage from '../components/FallbackImage';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const BASE_URL = `${API_URL}/api`;

function ProductListPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const categoryId = searchParams.get('category');

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [categoryTree, setCategoryTree] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sort, setSort] = useState('newest');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [priceInput, setPriceInput] = useState({ min: '', max: '' });
    const [categoryName, setCategoryName] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const PAGE_SIZE = 12;

    useEffect(() => {
        axios.get(`${BASE_URL}/categories`).then(res => setCategoryTree(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        setProducts([]);
        setCurrentPage(0);
        loadProducts(0, true);
    }, [categoryId, sort, minPrice, maxPrice, searchKeyword]);

    const loadProducts = async (page, reset = false) => {
        try {
            if (reset) setLoading(true);
            else setLoadingMore(true);

            const params = new URLSearchParams();
            params.append('page', page);
            params.append('size', PAGE_SIZE);
            params.append('sort', sort);
            if (categoryId) params.append('categoryId', categoryId);
            if (minPrice) params.append('minPrice', minPrice);
            if (maxPrice) params.append('maxPrice', maxPrice);
            if (searchKeyword) params.append('keyword', searchKeyword);

            const res = await axios.get(`${BASE_URL}/products?${params.toString()}`);

            if (reset) {
                setProducts(res.data.products);
            } else {
                setProducts(prev => [...prev, ...res.data.products]);
            }
            setTotalPages(res.data.totalPages);
            setTotalElements(res.data.totalElements);
            setCurrentPage(res.data.currentPage);

            if (categoryId) {
                for (const parent of categoryTree) {
                    if (String(parent.categoryId) === categoryId) { setCategoryName(parent.name); break; }
                    const child = parent.children?.find(c => String(c.categoryId) === categoryId);
                    if (child) { setCategoryName(child.name); break; }
                }
            } else {
                setCategoryName('');
            }
        } catch (err) {
            console.error('상품 로딩 실패', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleLoadMore = () => {
        const nextPage = currentPage + 1;
        loadProducts(nextPage, false);
    };

    const handlePriceFilter = () => {
        setMinPrice(priceInput.min);
        setMaxPrice(priceInput.max);
    };

    const handlePriceReset = () => {
    setPriceInput({ min: '', max: '' });
    setMinPrice('');
    setMaxPrice('');
    };

    const handleSearchInput = async (value) => {
        setSearchInput(value);
        if (value.length < 1) { setSuggestions([]); setShowSuggestions(false); return; }
        try {
            const res = await axios.get(`${BASE_URL}/products?keyword=${value}&size=5`);
            const names = [...new Set(res.data.products.map(p => p.name))].slice(0, 5);
            setSuggestions(names);
            setShowSuggestions(true);
        } catch { setSuggestions([]); }
    };

    const handleSuggestionClick = (name) => {
        setSearchInput(name);
        setSearchKeyword(name);
        setShowSuggestions(false);
    };

    const handleAllReset = () => {
        setPriceInput({ min: '', max: '' });
        setMinPrice('');
        setMaxPrice('');
        setSort('newest');
        setSearchKeyword('');
        setSearchInput('');
        setSearchParams({});
    };

    const handleCategoryClick = (id) => {
        setSearchParams(id ? { category: id } : {});
    };

    const highlightKeyword = (text, keyword) => {
        if (!keyword || !text) return text;
        const parts = text.split(new RegExp(`(${keyword})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === keyword.toLowerCase()
                ? <mark key={i} className={styles.highlight}>{part}</mark>
                : part
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.layout}>
                {/* 사이드바 */}
                <aside className={styles.sidebar}>
                    {(categoryId || minPrice || maxPrice || sort !== 'newest' || searchKeyword) && (
                        <button className={styles.resetAllBtn} onClick={handleAllReset}>
                            ✕ 필터 초기화
                        </button>
                    )}
                    <div className={styles.sideSection}>
                        <h4 className={styles.sideTitle}>카테고리</h4>
                        <div className={styles.categoryList}>
                            <div
                                className={`${styles.categoryItem} ${!categoryId ? styles.activeCategoryItem : ''}`}
                                onClick={() => handleCategoryClick(null)}>
                                전체
                            </div>
                            {categoryTree.map(parent => (
                                <div key={parent.categoryId}>
                                    <div
                                        className={`${styles.categoryItem} ${String(parent.categoryId) === categoryId ? styles.activeCategoryItem : ''}`}
                                        onClick={() => handleCategoryClick(parent.categoryId)}>
                                        {parent.name}
                                    </div>
                                    {parent.children?.map(child => (
                                        <div
                                            key={child.categoryId}
                                            className={`${styles.categoryChildItem} ${String(child.categoryId) === categoryId ? styles.activeCategoryItem : ''}`}
                                            onClick={() => handleCategoryClick(child.categoryId)}>
                                            {child.name}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 가격 필터 */}
                    <div className={styles.sideSection}>
                        <h4 className={styles.sideTitle}>가격</h4>
                        <div className={styles.priceInputs}>
                            <input
                                type="number"
                                placeholder="최소"
                                value={priceInput.min}
                                onChange={e => setPriceInput(prev => ({ ...prev, min: e.target.value }))}
                                className={styles.priceInput} />
                            <span className={styles.priceDash}>~</span>
                            <input
                                type="number"
                                placeholder="최대"
                                value={priceInput.max}
                                onChange={e => setPriceInput(prev => ({ ...prev, max: e.target.value }))}
                                className={styles.priceInput} />
                        </div>
                        <button className={styles.priceFilterBtn} onClick={handlePriceFilter}>적용</button>
                        {(minPrice || maxPrice) && (
                            <button className={styles.priceResetBtn} onClick={handlePriceReset}>초기화</button>
                        )}
                    </div>
                </aside>

                {/* 메인 콘텐츠 */}
                <main className={styles.main}>
                    <div className={styles.listHeader}>
                        <h2 className={styles.title}>
                            {categoryName || '전체 상품'}
                            <span className={styles.totalCount}> ({totalElements}개)</span>
                        </h2>
                        <select
                            value={sort}
                            onChange={e => setSort(e.target.value)}
                            className={styles.sortSelect}>
                            <option value="popular">인기순</option>
                            <option value="newest">신상품순</option>
                            <option value="price_asc">가격 낮은순</option>
                            <option value="price_desc">가격 높은순</option>
                        </select>
                    </div>
                    <div className={styles.searchBar} style={{ position: 'relative' }}>
                        <input
                            type="text"
                            placeholder="상품명을 검색해 주세요."
                            value={searchInput}
                            onChange={e => handleSearchInput(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') { setSearchKeyword(searchInput); setShowSuggestions(false); }
                                if (e.key === 'Escape') setShowSuggestions(false);
                            }}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                            className={styles.searchInput}
                        />
                        {showSuggestions && suggestions.length > 0 && (
                            <div style={{
                                position: 'absolute', top: '100%', left: 0,
                                background: 'white', border: '1px solid #ddd',
                                borderTop: 'none', width: '100%', zIndex: 100,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}>
                                {suggestions.map((name, i) => (
                                    <div key={i}
                                        onMouseDown={() => handleSuggestionClick(name)}
                                        style={{
                                            padding: '10px 14px', cursor: 'pointer',
                                            fontSize: '14px', color: '#333',
                                            borderBottom: '1px solid #f5f5f5'
                                        }}
                                        onMouseEnter={e => e.target.style.background = '#f9f9f9'}
                                        onMouseLeave={e => e.target.style.background = 'white'}>
                                        {highlightKeyword(name, searchInput)}
                                    </div>
                                ))}
                            </div>
                        )}
                        <button onClick={() => { setSearchKeyword(searchInput); setShowSuggestions(false); }} className={styles.searchBtn}>검색</button>
                        {searchKeyword && (
                            <button onClick={() => { setSearchKeyword(''); setSearchInput(''); setSuggestions([]); }} className={styles.searchResetBtn}>✕</button>
                        )}
                    </div>

                    {loading ? (
                        <p className={styles.loading}>로딩 중...</p>
                    ) : products.length === 0 ? (
                        <p className={styles.empty}>상품이 없습니다.</p>
                    ) : (
                        <>
                            <div className={styles.productGrid}>
                                {products.map(product => (
                                    <div key={product.productId} className={styles.productCard}
                                        onClick={() => navigate(`/products/${product.productId}`)}>
                                        <div className={styles.imageBox}>
                                            {product.discountRate > 0 && (
                                                <span className={styles.discountBadge}>{product.discountRate}%</span>
                                            )}
                                            <FallbackImage
                                                src={`${API_URL}${product.imageUrl}`}
                                                alt={product.name}
                                                className={styles.productImage}
                                            />
                                        </div>
                                        <div className={styles.productInfo}>
                                            <p className={styles.categoryName}>{product.categoryName}</p>
                                            <p className={styles.productName}>
                                                {highlightKeyword(product.name, searchKeyword)}
                                            </p>
                                            <div className={styles.priceRow}>
                                                {product.discountRate > 0 && (
                                                    <span className={styles.originalPrice}>{product.originalPrice?.toLocaleString()}원</span>
                                                )}
                                                <span className={styles.productPrice}>{product.price.toLocaleString()}원</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* 더보기 버튼 */}
                            {currentPage < totalPages - 1 && (
                                <div className={styles.loadMoreWrapper}>
                                    <button
                                        className={styles.loadMoreBtn}
                                        onClick={handleLoadMore}
                                        disabled={loadingMore}>
                                        {loadingMore ? '로딩 중...' : `더보기 (${products.length} / ${totalElements})`}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}

export default ProductListPage;